import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/core/auth/AuthContext';
import { toast } from 'sonner';
import { RequestsDetailService } from '../../detail/services/RequestsDetail.service';
import { RequestsEditService } from '../services/RequestsEdit.service';
import { RequestsNewService } from '../../new/services/RequestsNew.service';

export function useRequestsEdit() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const userId = profile?.id || '';
  const companyId = profile?.company_id || '';

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    zoneId: '',
    neededDate: '',
    priority: 'normal' as any,
    items: [] as any[],
  });

  const detailQuery = useQuery({
    queryKey: ['inventory', 'requests', 'detail', id],
    queryFn: () => RequestsDetailService.getRequest(id, userId),
    enabled: !!id && !!userId,
  });

  const projectsQuery = useQuery({
    queryKey: ['projects', 'simple-list', companyId],
    queryFn: () => RequestsNewService.getProjects(companyId),
    enabled: !!companyId,
  });

  const inventoryQuery = useQuery({
    queryKey: ['inventory', 'items-simple', companyId],
    queryFn: () => RequestsNewService.getInventoryItems(companyId),
    enabled: !!companyId,
  });

  useEffect(() => {
    if (detailQuery.data) {
      setFormData({
        title: detailQuery.data.title,
        description: detailQuery.data.description || '',
        projectId: detailQuery.data.project_id || '',
        zoneId: detailQuery.data.zone_id || '',
        neededDate: detailQuery.data.needed_date || '',
        priority: detailQuery.data.priority,
        items: (detailQuery.data.items || []).map((i: any) => ({
          ...i,
          inventoryItemId: i.inventory_item_id,
          requestedQuantity: i.requested_quantity,
          inventoryItem: i.inventory_item
        })),
      });
    }
  }, [detailQuery.data]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => RequestsEditService.updateRequest(userId, id, { ...data, companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'requests'] });
      toast.success(' cp nht yu cu');
      navigate(`/requests/detail/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Khng th cp nht');
    }
  });

  const addItem = (item: any) => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...item, id: Math.random().toString(36).substring(7) }]
    }));
  };

  const removeItem = (itemId: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(i => i.id !== itemId)
    }));
  };

  return {
    formData,
    projects: projectsQuery.data || [],
    inventoryItems: inventoryQuery.data || [],
    isLoading: detailQuery.isLoading || projectsQuery.isLoading || inventoryQuery.isLoading,
    isSaving: updateMutation.isPending,
    setFormData,
    addItem,
    removeItem,
    updateRequest: () => updateMutation.mutate(formData),
  };
}
